import { Prompt, GeoIntent, GeoUseCase } from "./types";

export type QualityLevel = 'basic' | 'good' | 'excellent' | 'verified';

export interface QualityBreakdownItem {
  field: string;
  points: number;
  maxPoints: number;
  completed: boolean;
}

export interface QualityScore {
  score: number;        // 0-100
  level: QualityLevel;
  breakdown: QualityBreakdownItem[];
  suggestions: string[];
}

// Partial prompt data for create form (before full Prompt object exists)
export interface PartialPromptData {
  title?: string;
  description?: string;
  content?: string;
  category?: string;
  geoMetadata?: {
    intent?: GeoIntent | "";
    use_case?: GeoUseCase | "";
    models?: { recommended?: string };
    reliability?: {
      tested_by_author?: boolean;
      community_verified?: number;
    };
  };
}

/**
 * Calculate GEO quality score based on metadata completeness
 * Works with both full Prompt objects and partial form data
 */
export const calculateGeoQualityScore = (prompt: Prompt | PartialPromptData): QualityScore => {
  const breakdown: QualityBreakdownItem[] = [];
  let totalScore = 0;
  const maxScore = 100;

  // Required fields (40 points)
  const hasTitle = !!(prompt.title?.trim() && prompt.title.trim().length >= 5);
  breakdown.push({ 
    field: 'Title', 
    points: hasTitle ? 15 : 0, 
    maxPoints: 15, 
    completed: hasTitle 
  });
  totalScore += hasTitle ? 15 : 0;

  const hasContent = (prompt.content?.length || 0) >= 50;
  breakdown.push({ 
    field: 'Content (50+ chars)', 
    points: hasContent ? 15 : 0, 
    maxPoints: 15, 
    completed: hasContent 
  });
  totalScore += hasContent ? 15 : 0;

  const hasIntent = !!prompt.geoMetadata?.intent;
  breakdown.push({ 
    field: 'GEO Intent', 
    points: hasIntent ? 10 : 0, 
    maxPoints: 10, 
    completed: hasIntent 
  });
  totalScore += hasIntent ? 10 : 0;

  // GEO fields (40 points)
  const hasUseCase = !!prompt.geoMetadata?.use_case;
  breakdown.push({ 
    field: 'GEO Use Case', 
    points: hasUseCase ? 10 : 0, 
    maxPoints: 10, 
    completed: hasUseCase 
  });
  totalScore += hasUseCase ? 10 : 0;

  const hasModel = !!prompt.geoMetadata?.models?.recommended;
  breakdown.push({ 
    field: 'Recommended Model', 
    points: hasModel ? 10 : 0, 
    maxPoints: 10, 
    completed: hasModel 
  });
  totalScore += hasModel ? 10 : 0;

  const hasDescription = !!(prompt.description?.trim() && prompt.description.trim().length >= 10);
  breakdown.push({ 
    field: 'Description', 
    points: hasDescription ? 10 : 0, 
    maxPoints: 10, 
    completed: hasDescription 
  });
  totalScore += hasDescription ? 10 : 0;

  const hasCategory = !!(prompt as any).category;
  breakdown.push({ 
    field: 'Category', 
    points: hasCategory ? 10 : 0, 
    maxPoints: 10, 
    completed: hasCategory 
  });
  totalScore += hasCategory ? 10 : 0;

  // Trust signals (20 points)
  const testedByAuthor = prompt.geoMetadata?.reliability?.tested_by_author || false;
  breakdown.push({ 
    field: 'Author Tested', 
    points: testedByAuthor ? 10 : 0, 
    maxPoints: 10, 
    completed: testedByAuthor 
  });
  totalScore += testedByAuthor ? 10 : 0;

  const communityVerified = (prompt.geoMetadata?.reliability?.community_verified || 0) >= 3;
  breakdown.push({ 
    field: 'Community Verified (3+)', 
    points: communityVerified ? 10 : 0, 
    maxPoints: 10, 
    completed: communityVerified 
  });
  totalScore += communityVerified ? 10 : 0;

  // Calculate percentage and level
  const percentage = Math.round((totalScore / maxScore) * 100);
  let level: QualityLevel = 'basic';
  if (percentage >= 90) level = 'verified';
  else if (percentage >= 70) level = 'excellent';
  else if (percentage >= 50) level = 'good';

  // Generate suggestions for incomplete fields
  const suggestions: string[] = [];
  breakdown.filter(b => !b.completed).forEach(b => {
    switch (b.field) {
      case 'Title':
        suggestions.push('Add a descriptive title (5+ characters)');
        break;
      case 'Content (50+ chars)':
        suggestions.push('Add more content (50+ characters)');
        break;
      case 'Description':
        suggestions.push('Add a description to help users understand your prompt');
        break;
      case 'Recommended Model':
        suggestions.push('Specify which AI model works best with this prompt');
        break;
      case 'Author Tested':
        suggestions.push('Mark as "Author Tested" if you\'ve verified it works');
        break;
      case 'Category':
        suggestions.push('Add a category for better discoverability');
        break;
      case 'GEO Intent':
        suggestions.push('Select a GEO intent for AI search visibility');
        break;
      case 'GEO Use Case':
        suggestions.push('Select a use case for better targeting');
        break;
    }
  });

  return { score: percentage, level, breakdown, suggestions };
};

/**
 * Get the color class for a quality level
 */
export const getQualityLevelColor = (level: QualityLevel): string => {
  switch (level) {
    case 'verified':
      return 'text-brand-cyan';
    case 'excellent':
      return 'text-purple-400';
    case 'good':
      return 'text-blue-400';
    default:
      return 'text-muted-foreground';
  }
};

/**
 * Get the background color class for a quality level
 */
export const getQualityLevelBgColor = (level: QualityLevel): string => {
  switch (level) {
    case 'verified':
      return 'bg-brand-cyan/20 border-brand-cyan/30';
    case 'excellent':
      return 'bg-purple-500/20 border-purple-500/30';
    case 'good':
      return 'bg-blue-500/20 border-blue-500/30';
    default:
      return 'bg-muted/20 border-muted/30';
  }
};

/**
 * Get the label for a quality level
 */
export const getQualityLevelLabel = (level: QualityLevel): string => {
  switch (level) {
    case 'verified':
      return '✨ Verified';
    case 'excellent':
      return '🟣 Excellent';
    case 'good':
      return '🔵 Good';
    default:
      return '⚪ Basic';
  }
};
